/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
import { apiMessageOrGeneric } from "../../utils/genericErrorMessages.js";
/**
 * @fileoverview Video Tutorial Section logic module.
 * Handles CRUD operations for video tutorial sections/categories
 * used to organize video tutorials.
 * @module logic/videoTutorialSection
 */

import { refreshToken, signOut } from "../../utils/common.js";

const UNKNOWN_ERROR = "Unknown error";

/**
 * Fetches all video tutorial sections from the API
 * @param {Function} navigate - Navigation function
 * @returns {Promise<Array>} Array of video sections
 */
export const getVideoSections = async (navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}videoTutorialSection`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getVideoSections(navigate);
    }

    if (!response.ok) {
      logger.error("Error fetching video sections:", {
        data: data?.message || UNKNOWN_ERROR,
      });
      return [];
    }

    if (Array.isArray(data)) {
      return data;
    }
    return [];
  } catch (error) {
    logger.error("Error fetching video sections:", {
      err: sanitizeError(error),
    });
    return [];
  }
};

/**
 * Fetches a video tutorial section by ID from the API
 * @param {string} sectionId - The ID of the video tutorial section
 * @param {Function} navigate - Navigation function
 * @returns {Promise<Object|null>} Video tutorial section object or null if not found
 */
export const getVideoSectionById = async (sectionId, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}videoTutorialSection/${sectionId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await getVideoSectionById(sectionId, navigate);
    }

    if (!response.ok) {
      logger.error("Error fetching video section:", {
        data: data?.message || UNKNOWN_ERROR,
      });
      return null;
    }

    return data;
  } catch (error) {
    logger.error("Error fetching video section:", {
      err: sanitizeError(error),
    });
    return null;
  }
};

/**
 * Creates a new video tutorial section
 * @param {Object} sectionData - Section data with name field
 * @param {Function} navigate - Navigation function
 * @returns {Promise<Object|null>} Created section object or null on error
 */
export const createVideoSection = async (sectionData, navigate) => {
  const options = {
    method: "POST",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(sectionData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}videoTutorialSection`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await createVideoSection(sectionData, navigate);
    }

    if (!response.ok) {
      logger.error("Error creating video section:", {
        data: data?.message || UNKNOWN_ERROR,
      });
      throw new Error(
        apiMessageOrGeneric(data?.message, "FAILED_TO_CREATE_VIDEO_SECTION"),
      );
    }

    return data.section || data;
  } catch (error) {
    logger.error("Error creating video section:", {
      err: sanitizeError(error),
    });
    throw error;
  }
};

/**
 * Updates an existing video tutorial section
 * @param {string} sectionId - The ID of the video tutorial section to update
 * @param {Object} sectionData - Updated section data with name field
 * @param {Function} navigate - Navigation function
 * @returns {Promise<Object|null>} Updated section object or null on error
 */
export const updateVideoSection = async (sectionId, sectionData, navigate) => {
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: JSON.stringify(sectionData),
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}videoTutorialSection/${sectionId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return null;
      }
      return await updateVideoSection(sectionId, sectionData, navigate);
    }

    if (!response.ok) {
      logger.error("Error updating video section:", {
        data: data?.message || UNKNOWN_ERROR,
      });
      throw new Error(
        apiMessageOrGeneric(data?.message, "FAILED_TO_UPDATE_VIDEO_SECTION"),
      );
    }

    return data.section || data;
  } catch (error) {
    logger.error("Error updating video section:", {
      err: sanitizeError(error),
    });
    throw error;
  }
};

/**
 * Deletes a video tutorial section and all associated video tutorials
 * @param {string} sectionId - The ID of the video tutorial section to delete
 * @param {Function} navigate - Navigation function
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const deleteVideoSection = async (sectionId, navigate) => {
  const options = {
    method: "DELETE",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}videoTutorialSection/${sectionId}`,
      options,
    );
    const data = await response.json();

    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return false;
      }
      return await deleteVideoSection(sectionId, navigate);
    }

    if (!response.ok) {
      logger.error("Error deleting video section:", {
        data: data?.message || UNKNOWN_ERROR,
      });
      return false;
    }

    return true;
  } catch (error) {
    logger.error("Error deleting video section:", {
      err: sanitizeError(error),
    });
    return false;
  }
};
